import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Path, Svg } from 'react-native-svg';
import { typeColors, TypeVariant } from './types';

export interface TypeIconProps {
  /**
   * The type variant that determines the icon and color
   */
  variant: TypeVariant;

  /**
   * Size of the icon in pixels (12 or 16)
   */
  size?: 12 | 16;

  /**
   * Optional color override (defaults to type color)
   */
  color?: string;

  /**
   * Test ID for testing
   */
  testID?: string;
}

/**
 * TypeIcon - SVG icon for type status
 *
 * From Figma Crop Circle System - Types component
 * Node: 343-77
 */
export const TypeIcon: React.FC<TypeIconProps> = ({
  variant,
  size = 16,
  color,
  testID,
}) => {
  // Normalize variant to base type for icon lookup
  const baseVariant = normalizeVariant(variant);
  const fillColor = color || typeColors[variant];

  return (
    <View testID={testID} accessibilityRole="image" accessibilityLabel={`${variant} type indicator`}>
      <Svg width={size} height={size} viewBox="0 0 16 16" fill="none">
        {getIconPath(baseVariant, fillColor)}
      </Svg>
    </View>
  );
};

// Normalize aliases to base variants
function normalizeVariant(variant: TypeVariant): 'attention' | 'blocker' | 'done' | 'routines' | 'note' | 'campaign' {
  switch (variant) {
    case 'urgent':
      return 'attention';
    case 'pending':
      return 'blocker';
    case 'solved':
      return 'done';
    default:
      return variant as 'attention' | 'blocker' | 'done' | 'routines' | 'note' | 'campaign';
  }
}

// Get SVG path for each type
function getIconPath(variant: string, fillColor: string): React.ReactNode {
  switch (variant) {
    case 'attention':
      return (
        <Path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M8 0C12.4183 0 16 3.58172 16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0ZM8 10.5C7.44772 10.5 7 10.9477 7 11.5C7 12.0523 7.44772 12.5 8 12.5C8.55229 12.5 9 12.0523 9 11.5C9 10.9477 8.55229 10.5 8 10.5ZM8 3.5C7.44772 3.5 7 3.94772 7 4.5V8.5C7 9.05229 7.44772 9.5 8 9.5C8.55229 9.5 9 9.05229 9 8.5V4.5C9 3.94772 8.55229 3.5 8 3.5Z"
          fill={fillColor}
        />
      );
    case 'blocker':
      return (
        <Path
          d="M8 0C12.4183 0 16 3.58172 16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0ZM4 7V9H12V7H4Z"
          fill={fillColor}
        />
      );
    case 'done':
      return (
        <Path
          d="M8 0C12.4183 0 16 3.58172 16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0ZM6.61914 9.38086L4.23828 7L3 8.23828L6.61914 11.8564L12.2383 6.23828L11 5L6.61914 9.38086Z"
          fill={fillColor}
        />
      );
    case 'routines':
      return (
        <Path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M16 15C16 15.5523 15.5523 16 15 16H1C0.447715 16 0 15.5523 0 15V1C0 0.447715 0.447715 0 1 0H15C15.5523 0 16 0.447715 16 1V15ZM6.95312 9.04818L4.57161 6.66667L3.33333 7.90495L6.95312 11.5234L12.5716 5.90495L11.3333 4.66667L6.95312 9.04818Z"
          fill={fillColor}
        />
      );
    case 'note':
      return (
        <Path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M16 15C16 15.5523 15.5523 16 15 16H1C0.447715 16 0 15.5523 0 15V1C0 0.447715 0.447715 0 1 0H15C15.5523 0 16 0.447715 16 1V15ZM8 5.33333C6.52724 5.33333 5.33333 6.52724 5.33333 8C5.33333 9.47276 6.52724 10.6667 8 10.6667C9.47276 10.6667 10.6667 9.47276 10.6667 8C10.6667 6.52724 9.47276 5.33333 8 5.33333Z"
          fill={fillColor}
        />
      );
    case 'campaign':
      return (
        <Path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M16 15C16 15.5523 15.5523 16 15 16H1C0.447715 16 0 15.5523 0 15V1C0 0.447715 0.447715 0 1 0H15C15.5523 0 16 0.447715 16 1V15ZM4.51302 4.51172C4.05502 4.96972 3.69063 5.5136 3.44271 6.11198C3.19484 6.71041 3.06775 7.35227 3.06771 8C3.06771 8.64779 3.19484 9.28953 3.44271 9.88802C3.69063 10.4866 4.05492 11.0302 4.51302 11.4883L5.45573 10.5456C5.12144 10.2113 4.8554 9.81438 4.67448 9.3776C4.49366 8.94093 4.40104 8.47263 4.40104 8C4.40108 7.52743 4.49366 7.05901 4.67448 6.6224C4.85539 6.18578 5.12154 5.78861 5.45573 5.45443L4.51302 4.51172ZM10.5469 5.45443C10.881 5.78864 11.1459 6.18577 11.3268 6.6224C11.5077 7.05907 11.6015 7.52735 11.6016 8C11.6016 8.4726 11.5076 8.94096 11.3268 9.3776C11.146 9.81414 10.8809 10.2114 10.5469 10.5456L11.4896 11.4883C11.9474 11.0303 12.3108 10.4863 12.5586 9.88802C12.8064 9.2896 12.9349 8.64771 12.9349 8C12.9349 7.35226 12.8065 6.71042 12.5586 6.11198C12.3107 5.51365 11.9475 4.9697 11.4896 4.51172L10.5469 5.45443ZM8 6.66667C7.26397 6.66695 6.66684 7.26395 6.66667 8C6.66667 8.7362 7.26386 9.33305 8 9.33333C8.73638 9.33333 9.33333 8.73638 9.33333 8C9.33316 7.26377 8.73627 6.66667 8 6.66667Z"
          fill={fillColor}
        />
      );
    default:
      return null;
  }
}

TypeIcon.displayName = 'TypeIcon';
